import React from "react";
import {
  DropdownField,
  FormSection,
  TextField,
  useToast,
} from "../../../components/ui";
import {
  normaliseEmailInput,
  normalisePhoneInput,
  normalisePincodeInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import { STATE_OPTIONS, citiesOfState } from "../../../lib/locations";
import type { BillingDetails } from "../types/billing.types";
import {
  BillingErrors,
  ValidatedField,
  normaliseGstinInput,
} from "../validation";

/**
 * The validated fields in the order they are laid out, which is the order the
 * first problem has to be looked for in — `VALIDATED_FIELDS` is grouped by
 * required-ness instead, and would send you to the wrong one.
 */
export const FIELD_ORDER: ValidatedField[] = [
  "fullName",
  "phone",
  "email",
  "gstin",
  "address",
  "city",
  "state",
  "pincode",
];

/** Stable id per control, so a failing field can be reached and focused. */
export const controlId = (field: ValidatedField) => `billing-${field}`;

interface BillingDetailsFormProps {
  details: BillingDetails;
  /** Patch merge — every control sends only the key it owns. */
  onChange: (patch: Partial<BillingDetails>) => void;
  /** Every failing field, whether or not it is being shown one. */
  errors: BillingErrors;
  /**
   * Which fields have been visited. An error only surfaces for a field in
   * here, so the form does not open covered in red before anything has been
   * typed.
   *
   * Held by the screen rather than by this component, because the button that
   * submits the form stands in the summary beside it: a failed submit has to
   * be able to mark every field visited at once, and it cannot reach in here
   * to do it.
   */
  touched: Partial<Record<ValidatedField, boolean>>;
  onVisit: (field: ValidatedField) => void;
  /** True while the purchase is with the server — the form stands down. */
  busy?: boolean;
}

/**
 * Who the subscription is billed to: who they are, then where they are.
 *
 * Two cards rather than one long form, because the second half is answering a
 * different question from the first — and a card is where this app puts a group
 * of fields that belong together.
 *
 * Every field an invoice cannot be raised without wears a red asterisk, and the
 * GSTIN — the one field that is genuinely optional — wears nothing. It is the
 * mark the rest of the app's forms use, so a required field looks the same here
 * as it does on `My brand` or `Add client`. `REQUIRED_FIELDS` is what actually
 * decides; the asterisks only have to agree with it.
 *
 * The controls and normalisers are the app's own, the same ones the brand form
 * is built from — a phone number is a phone number wherever it is asked for,
 * and this screen having its own would be how the two quietly stop agreeing
 * about what one looks like. The address is given the same way too: a state
 * picked from `locations`, and a city picked from the list that state fills.
 *
 * It holds no state at all: the values belong to the screen, which is what has
 * to submit them, and so does the record of what has been visited.
 */
const BillingDetailsForm: React.FC<BillingDetailsFormProps> = ({
  details,
  onChange,
  errors,
  touched,
  onVisit,
  busy = false,
}) => {
  const toast = useToast();

  const visit = (field: ValidatedField) => () => onVisit(field);

  const errorFor = (field: ValidatedField) =>
    touched[field] ? errors[field] : undefined;

  // The city list belongs to the state, so changing the state drops whatever
  // city was picked under the old one — keeping it would leave the invoice
  // naming a city that is not in the state beneath it. The same handler
  // `BrandDetailsForm` and `AddClientForm` keep, for the same reason.
  const handleStateChange = (state: string) => onChange({ state, city: "" });

  return (
    <div className="flex flex-col gap-5 3xl:gap-6">
      <FormSection title="Basic details">
        <TextField
          id={controlId("fullName")}
          label="Full name"
          required
          placeholder="eg. Riya Roy"
          value={details.fullName}
          disabled={busy}
          onValueChange={(value) => onChange({ fullName: toNameCase(value) })}
          onBlur={visit("fullName")}
          error={errorFor("fullName")}
        />

        {/* Three to a row from `sm` up — the three short answers about how to
            reach and bill this person, which the name above spans. */}
        <div className="mt-4 3xl:mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3 3xl:gap-5">
          <TextField
            id={controlId("phone")}
            label="Phone number"
            required
            prefix="+91"
            inputMode="numeric"
            placeholder=" 7869369854"
            maxLength={10}
            value={details.phone}
            disabled={busy}
            onValueChange={(value) =>
              onChange({ phone: normalisePhoneInput(value) })
            }
            onBlur={visit("phone")}
            error={errorFor("phone")}
          />
          <TextField
            id={controlId("email")}
            label="Email"
            required
            type="email"
            placeholder="riyaroy@gmail.com"
            value={details.email}
            disabled={busy}
            onValueChange={(value) =>
              onChange({ email: normaliseEmailInput(value) })
            }
            onBlur={visit("email")}
            error={errorFor("email")}
          />
          <TextField
            id={controlId("gstin")}
            // No asterisk and no "(optional)" either: with the other seven
            // marked, the field without a mark is the optional one, and saying
            // it twice would be the same fact in two notations.
            label="GSTIN"
            placeholder="27ABCDE1234FZ16"
            value={details.gstin}
            disabled={busy}
            onValueChange={(value) =>
              onChange({ gstin: normaliseGstinInput(value) })
            }
            onBlur={visit("gstin")}
            error={errorFor("gstin")}
          />
        </div>
      </FormSection>

      <FormSection title="Address details">
        <TextField
          id={controlId("address")}
          label="Street address"
          required
          placeholder="eg. 123, Ram nagar"
          value={details.address}
          disabled={busy}
          onValueChange={(value) => onChange({ address: value })}
          onBlur={visit("address")}
          error={errorFor("address")}
        />

        <div className="mt-4 3xl:mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3 3xl:gap-5">
          {/* Both picked from a list, and the city's is filled by the state —
              the same pair `My brand` and `Add client` draw, off the
              same `locations` data, so an address is given the same way
              wherever the app asks for one.

              **State first, and that is the whole reason for the order.** The
              city list has nothing in it until a state is picked, so a form
              that put the city first would open on a control that cannot be
              used and send everybody back a field to find out why. Reading
              left to right is now the order the two have to be answered in.

              The city was typed here once, on the argument that a place the
              app's list has never heard of is still where somebody lives. Being
              the odd form out was the worse of the two: a dietitian who has
              given their address on three other screens should not meet a
              different control on the fourth. Both are searchable, since 34
              states and up to 120 cities are more than anyone wants to scroll.
              The state matters twice over — it decides the place of supply on
              the invoice — which is why it was already a closed list. */}
          <DropdownField
            id={controlId("state")}
            label="State"
            required
            searchable
            placeholder="Select state"
            options={STATE_OPTIONS}
            value={details.state}
            disabled={busy}
            onValueChange={handleStateChange}
            onBlur={visit("state")}
            error={errorFor("state")}
          />
          <DropdownField
            id={controlId("city")}
            label="City"
            required
            searchable
            // The list covers the common cases and cannot claim to be
            // exhaustive: a place it has never heard of is still where somebody
            // lives, which is the reason this field was free text to begin
            // with. It also matters twice over here — a billing profile saved
            // before the list existed can be holding a city the list has no
            // entry for, the dataset spells Bengaluru that way and has no
            // "Bangalore" at all. Without this such an address would open
            // blank, and pressing the button would send an empty city to the
            // invoice. Every other address field in the app takes one the same
            // way.
            creatable
            placeholder={details.state ? "eg. Mumbai" : "Select a state first"}
            options={citiesOfState(details.state)}
            value={details.city}
            disabled={busy || !details.state}
            // Reaching for the city before there is a state to fill it is a
            // reasonable thing to do, so it gets an answer rather than a
            // control that simply refuses to respond. Only when it is the
            // missing state holding it shut — while the form is busy, every
            // control is off and the button that turned them off is the answer.
            onDisabledClick={
              busy
                ? undefined
                : () =>
                    toast.warning(
                      "Select a state first",
                      "The city list is filled by the state you pick."
                    )
            }
            onValueChange={(city) => onChange({ city })}
            onBlur={visit("city")}
            error={errorFor("city")}
          />
          <TextField
            id={controlId("pincode")}
            label="ZIP/Postal code"
            required
            inputMode="numeric"
            placeholder="eg. 410225"
            value={details.pincode}
            disabled={busy}
            onValueChange={(value) =>
              onChange({ pincode: normalisePincodeInput(value) })
            }
            onBlur={visit("pincode")}
            error={errorFor("pincode")}
          />
        </div>
      </FormSection>
    </div>
  );
};

export default BillingDetailsForm;
